import React, { ComponentType, SVGProps } from "react";
import * as LucideIcons from "lucide-react";
import { kebabToPascalCase } from "../../utils/iconHelpers";

interface NotificationItemProps {
  title: string;
  content: string;
  date?: string;
  icon?: string;
  colorClass?: string;
  isRead?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  onMarkAsRead?: () => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  title,
  content,
  date,
  icon,
  colorClass = "bg-slate-100",
  isRead = false,
  isLoading = false,
  onClick,
  onMarkAsRead,
}) => {
  const iconName = icon ? kebabToPascalCase(icon) : "Bell";
  const IconComponent = (
    LucideIcons[iconName as keyof typeof LucideIcons] || LucideIcons.Bell
  ) as ComponentType<SVGProps<SVGSVGElement>>;

  const handleClick = () => {
    if (!isRead && onMarkAsRead) {
      onMarkAsRead();
    }
    onClick?.();
  };

  return (
    <div
      onClick={handleClick}
      className={`px-4 py-3 transition-all duration-150 cursor-pointer ${isRead
        ? "hover:bg-slate-50"
        : "bg-emerald-50/30 hover:bg-emerald-100/50"
        } ${isLoading ? "opacity-60 cursor-not-allowed" : ""} group`}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-150 ${colorClass}
              ${!isRead ? "ring-1 ring-current ring-opacity-20" : "opacity-75"}`}
          >
            <IconComponent className="w-5 h-5 text-gray-700" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 overflow-hidden">
              <p
                className={`text-sm font-medium line-clamp-1 transition-colors ${isRead
                  ? "text-gray-700"
                  : "text-emerald-900 font-semibold"
                  } break-words`}
              >
                {title}
              </p>

              <p
                className={`text-sm line-clamp-2 mt-1 transition-colors ${isRead
                  ? "text-gray-500"
                  : "text-emerald-800"
                  } break-words`}
              >
                {content}
              </p>
            </div>

            {!isRead && (
              <div className="w-2.5 h-2.5 bg-emerald-600 rounded-full flex-shrink-0 mt-1 shadow-sm shadow-emerald-600/50 animate-pulse" />
            )}
          </div>

          {date && (
            <p className={`text-xs mt-1.5 transition-colors ${isRead ? "text-gray-400" : "text-gray-500"}`}>
              {date}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
